import { forwardRef } from 'react'

/**
 * Top-of-form summary that lists every field-level error as a link to the
 * offending input. Focused on submit-failure so screen readers announce the
 * full list together rather than the user discovering errors one-by-one.
 *
 * Mirrors the GOV.UK Design System error-summary pattern (WCAG 3.3.1, 3.3.3).
 *
 * Callers own the ref so they can move focus to the summary after a failed
 * submit:
 *
 *   const ref = useRef<HTMLDivElement>(null)
 *   window.requestAnimationFrame(() => ref.current?.focus())
 *   <ErrorSummary ref={ref} items={...} />
 */

export type ErrorSummaryItem = {
  /** React key — usually the field key. */
  key: string
  /** Field label rendered before the message (e.g. "Email"). */
  label: string
  /** The error message itself. */
  message: string
  /** DOM id of the field to focus when the link is clicked. */
  targetId: string
}

export type ErrorSummaryProps = {
  /** When empty, the component renders nothing. */
  items: ErrorSummaryItem[]
  /** Heading copy. Defaults to standard "Please fix…" line. */
  heading?: string
  className?: string
}

export const ErrorSummary = forwardRef<HTMLDivElement, ErrorSummaryProps>(function ErrorSummary(
  { items, heading = 'Please fix the following before continuing:', className = '' },
  ref,
) {
  if (items.length === 0) return null
  const cls = ['al-error-summary', className].filter(Boolean).join(' ')
  return (
    <div ref={ref} role="alert" tabIndex={-1} className={cls}>
      <p className="xsmall">
        <strong>{heading}</strong>
      </p>
      <ul>
        {items.map((item) => (
          <li key={item.key} className="no-m p-lx2">
            <a
              href={`#${item.targetId}`}
              className="xsmall link-reset accent"
              onClick={(e) => {
                // Inside HashRouter, native anchor clicks would be
                // interpreted as route changes — bypass the browser and
                // focus the target manually.
                e.preventDefault()
                const target = document.getElementById(item.targetId)
                if (target) {
                  target.focus({ preventScroll: true })
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
              }}
            >
              {item.label}: {item.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
})
